import React, { useEffect, useState } from 'react';
import { Plus, X } from 'lucide-react';
import BrandDropdown from 'shared/components/ui/BrandDropdown';
import SegmentedTabs from 'shared/components/ui/SegmentedTabs';
import SliderRow from 'shared/components/ui/SliderRow';
import { fetchAgentRunPropertyValues } from 'shared/utils/filters/agentRunPropertyValues';
import { stepsGroup } from './graphModel';
import { readStatement } from 'features/evals3/api/evals3Api';
import {
  CHECKS, DEFAULT_THRESHOLD, SPANS_KEY, PATH_KEY, checkOf, defaultsFor, describeReading, keyOf, selectorFor, statementOf, statementSources,
  withStatement, textSources, judgedSources, vectorSources,
} from './semantic';

/**
 * The Semantic check's settings, in the same shape as the other step
 * editors: one control per decision, a short title each, nothing to read.
 *
 *   Grounded   Answer (one text of this level) · Context · claims supported
 *   Intent     About (input and output, one of them, or the transcript) ·
 *              the intent, and how it reads · confidence
 *   Matches    Text · Examples · similarity
 *
 * Every check is true or false: "True at" is the one cut the person picks.
 *
 * Only a Grounded check's CONTEXT may be many spans: every span matching a
 * name or kind, newest N, input / output / both. See ./semantic.js.
 */

const inputCls = 'h-8 w-full rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-base)] px-2.5 text-[12.5px] text-[color:var(--text-primary)] outline-none placeholder:text-[color:var(--text-muted)] focus:border-[color:var(--border-strong)] disabled:opacity-60';

function Section({ title, children, aside = null }) {
  return (
    <section className="mb-5">
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-[color:var(--text-primary)]">{title}</h3>
        {aside}
      </div>
      {children}
    </section>
  );
}

/** A text box that commits on blur, so a keystroke is not a save. */
function Committed({ value, onChange, as = 'input', ...rest }) {
  const [text, setText] = useState(value);
  useEffect(() => { setText(value); }, [value]);
  const Tag = as;
  return <Tag value={text} onChange={(e) => setText(e.target.value)} onBlur={() => { if (text !== value) onChange(text); }} {...rest} />;
}

/** Span names or kinds this team actually has, for the picker. */
function useSpanValues(teamId, field, agentName) {
  const [values, setValues] = useState([]);
  useEffect(() => {
    let alive = true;
    if (!teamId) return undefined;
    Promise.resolve(fetchAgentRunPropertyValues({ teamId, field, agentName, limit: 100 }))
      .then((vs) => { if (alive) setValues((vs || []).map((v) => (typeof v === 'object' && v !== null ? String(v.value) : String(v))).filter(Boolean)); })
      .catch(() => { if (alive) setValues([]); });
    return () => { alive = false; };
  }, [teamId, field, agentName]);
  return values;
}

/** Which spans feed the context: matched by name or kind, newest N, which side of each. */
function SpansPicker({ value, onChange, readOnly, teamId, agentName }) {
  const by = 'name' in value.spans ? 'name' : 'kind';   // an empty name is still "by name", mid-pick
  const names = useSpanValues(teamId, by === 'name' ? 'span.name' : 'span.kind', agentName);
  const current = value.spans[by] || '';
  const options = [...new Set([...(current ? [current] : []), ...names])].map((v) => ({ value: v, label: v }));
  const set = (patch) => onChange({ ...value, ...patch });
  return (
    <div className="mt-2 space-y-2 rounded-md border border-[color:var(--border-default)] p-2.5">
      <div className="flex items-center gap-2">
        <SegmentedTabs size="sm" ariaLabel="Match spans by" value={by}
          onChange={(k) => { if (!readOnly && k !== by) set({ spans: { [k]: k === 'kind' ? 'tool' : '' } }); }}
          options={[{ value: 'kind', label: 'Kind' }, { value: 'name', label: 'Name' }]} />
        <div className="min-w-0 flex-1">
          <BrandDropdown compact searchable ariaLabel={`Span ${by}`} value={current} disabled={readOnly} placeholder={by === 'name' ? 'Pick a span name' : 'Pick a kind'}
            menuMinWidth={260} onChange={(v) => set({ spans: { [by]: v } })} options={options} />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <SegmentedTabs size="sm" ariaLabel="Span side" value={value.side || 'output'} onChange={(side) => { if (!readOnly) set({ side }); }}
          options={[{ value: 'input', label: 'Input' }, { value: 'output', label: 'Output' }, { value: 'both', label: 'Both' }]} />
        <div className="flex-1" />
        <label className="flex items-center gap-1.5 text-[11.5px] text-[color:var(--text-secondary)]" title="When more spans match, only the newest are read">
          Newest
          <input type="number" min={1} max={200} aria-label="Newest spans" disabled={readOnly} value={value.last ?? 20}
            onChange={(e) => { const n = Math.floor(Number(e.target.value)); if (n >= 1 && n <= 200) set({ last: n }); }}
            className="h-7 w-14 rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-base)] px-1.5 text-center font-mono text-[12px] tabular-nums text-[color:var(--text-primary)] outline-none focus:border-[color:var(--border-strong)]" />
        </label>
      </div>
    </div>
  );
}

/** One source of text: a dropdown, plus what a spans or path choice needs. */
function Source({ label, value, onChange, options, readOnly, teamId, agentName }) {
  const key = keyOf(value);
  return (
    <>
      <BrandDropdown ariaLabel={label} value={key} disabled={readOnly} menuMinWidth={240} onChange={(k) => onChange(selectorFor(k, value))} options={options} />
      {key === SPANS_KEY && <SpansPicker value={value} onChange={onChange} readOnly={readOnly} teamId={teamId} agentName={agentName} />}
      {key === PATH_KEY && (
        <Committed aria-label={`${label}: path`} value={typeof value === 'string' ? value : ''} disabled={readOnly} placeholder="run.metadata.retrieved_context"
          onChange={(v) => onChange(v.trim())} className={`${inputCls} mt-2 font-mono`} />
      )}
    </>
  );
}

/** Example texts, one row each. */
function Examples({ texts, onChange, readOnly }) {
  const [draft, setDraft] = useState('');
  const add = () => { const t = draft.trim(); if (t && !texts.includes(t)) onChange([...texts, t]); setDraft(''); };
  return (
    <div className="space-y-1.5">
      {texts.map((t, i) => (
        <div key={`${i}-${t}`} className="flex items-center gap-1.5">
          <Committed aria-label={`Example ${i + 1}`} value={t} disabled={readOnly}
            onChange={(v) => { const x = v.trim(); onChange(x ? texts.map((y, j) => (j === i ? x : y)) : texts.filter((_, j) => j !== i)); }}
            className={inputCls} />
          {!readOnly && (
            <button type="button" aria-label={`Remove example ${i + 1}`} onClick={() => onChange(texts.filter((_, j) => j !== i))}
              className="shrink-0 rounded p-1 text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-danger"><X size={13} /></button>
          )}
        </div>
      ))}
      {!readOnly && (
        <div className="flex items-center gap-1.5">
          <input aria-label="Add an example" value={draft} placeholder="Add an example text" onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} onBlur={add}
            className={`${inputCls} border-dashed`} />
          <button type="button" aria-label="Add example" onClick={add} disabled={!draft.trim()}
            className="shrink-0 rounded p-1 text-[color:var(--text-muted)] hover:bg-surface-hover disabled:opacity-40"><Plus size={13} /></button>
        </div>
      )}
    </div>
  );
}

/** What "True at" measures, per check, and the range it moves in. */
const CUT = {
  grounded: { label: 'Claims supported', min: 0.5, max: 1, pct: true },
  intent: { label: 'Confidence', min: 0.05, max: 0.99 },
  resembles: { label: 'Similarity', min: 0.5, max: 1 },
};

/** How the server will read an intent, fetched as the person pauses typing. */
function useReading(teamId, statement) {
  const [reading, setReading] = useState(null);
  useEffect(() => {
    const text = String(statement || '').trim();
    if (!teamId || !text) { setReading(null); return undefined; }
    let alive = true;
    const timer = setTimeout(() => {
      Promise.resolve(readStatement(teamId, text))
        .then((r) => { if (alive) setReading(r || null); })
        .catch(() => { if (alive) setReading(null); });
    }, 350);
    return () => { alive = false; clearTimeout(timer); };
  }, [teamId, statement]);
  return reading;
}

/** The intent, and the line under it that says how it will be read. */
function StatementSection({ node, update, lvl, readOnly, teamId, agentName }) {
  const st = statementOf(node);
  const reading = useReading(teamId, st.intent);
  const shown = describeReading(reading, st.text, lvl);
  return (
    <>
      <Section title="About">
        <Source label="About" value={st.text} onChange={(text) => update(withStatement(node, { text }))} options={statementSources(lvl)} readOnly={readOnly} teamId={teamId} agentName={agentName} />
      </Section>
      <Section title="Intent">
        <Committed as="textarea" rows={2} aria-label="Intent" value={st.intent} disabled={readOnly}
          placeholder="Was the user's question answered?" onChange={(intent) => update(withStatement(node, { intent: intent.trim() }))}
          className={`${inputCls} h-auto min-h-[56px] resize-y py-2 leading-snug`} />
        {shown && (
          <p data-testid="statement-reading" className="mt-1.5 text-[11.5px] text-[color:var(--text-secondary)]">
            Reads as {shown.label}
            {shown.problem && <span className="text-danger"> · {shown.problem}</span>}
          </p>
        )}
      </Section>
    </>
  );
}

export default function SemanticEditor({ node, update, target, graph, nodeId, readOnly, teamId = null, agentName = null }) {
  const check = checkOf(node);
  const cfg = node.config || {};
  const before = graph && nodeId ? stepsGroup(graph, nodeId) : [];
  const defaultEdge = (node.edges || []).find((e) => e.default !== undefined);
  const lvl = target || 'run';

  // Another check is another config (and maybe instrument). Every check answers true or false, so a
  // node that already is one keeps its field and its routing; an older shape keeps only its default.
  const setCheck = (next) => {
    if (readOnly || next === check) return;
    const fresh = defaultsFor(next, lvl);
    if (check) {
      const field = Object.keys(node.output || {})[0] || 'ok';
      update({ ...fresh, output: { [field]: { type: 'boolean' } }, edges: node.edges || [] });
    } else {
      update({ ...fresh, edges: defaultEdge ? [defaultEdge] : [] });
    }
  };
  const tabs = CHECKS.map((c) => ({ value: c.id, label: c.label, hint: c.question }));

  if (!check) {
    return (
      <Section title="Check">
        <SegmentedTabs size="sm" ariaLabel="Check" value="" onChange={setCheck} options={tabs} />
        <p className="mt-2 text-[11.5px] text-[color:var(--text-muted)]">
          Saved in an older form ({node.instrument === 'bi_encoder' ? cfg.op || 'similarity' : 'entailment scores'}); it runs as saved. Pick a check to replace it.
        </p>
      </Section>
    );
  }

  const setPair = (i, sel) => update({ config: { ...cfg, pair: cfg.pair.map((p, j) => (j === i ? sel : p)) } });
  const setCfg = (patch) => update({ config: { ...cfg, ...patch } });
  const threshold = typeof cfg.threshold === 'number' ? cfg.threshold : DEFAULT_THRESHOLD[check];
  const cut = CUT[check];
  const src = { readOnly, teamId, agentName };

  return (
    <>
      <Section title="Check">
        <SegmentedTabs size="sm" ariaLabel="Check" value={check} onChange={setCheck} options={tabs} />
      </Section>

      {check === 'grounded' && (
        <>
          <Section title="Answer">
            <Source label="Answer" value={cfg.pair[1]} onChange={(sel) => setPair(1, sel)} options={judgedSources(lvl)} {...src} />
          </Section>
          <Section title="Context">
            <Source label="Context" value={cfg.pair[0]} onChange={(sel) => setPair(0, sel)} options={textSources(lvl, before)} {...src} />
          </Section>
        </>
      )}

      {check === 'intent' && <StatementSection node={node} update={update} lvl={lvl} {...src} />}

      {check === 'resembles' && (
        <>
          <Section title="Text">
            <BrandDropdown ariaLabel="Text" value={cfg.vector_of || vectorSources(lvl)[0].value} disabled={readOnly}
              onChange={(vector_of) => setCfg({ vector_of })} options={vectorSources(lvl)} />
          </Section>
          <Section title="Examples">
            <Examples texts={cfg.against?.texts || []} readOnly={readOnly} onChange={(texts) => setCfg({ against: { texts } })} />
          </Section>
        </>
      )}

      <Section title="True at">
        <SliderRow label={cut.label} value={threshold} display={cut.pct ? `${Math.round(threshold * 100)}%` : threshold.toFixed(2)}
          min={cut.min} max={cut.max} step={0.01} disabled={readOnly}
          onChange={(n) => (check === 'intent' ? update(withStatement(node, { threshold: Number(n) })) : setCfg({ threshold: Number(n) }))} />
      </Section>
    </>
  );
}
